import React, { useState } from "react";
import PassTemplate from "../Modals/PassTemplate";
import { useTranslation } from "react-i18next";

interface DetailsProps {
  activeTab: string;
  passTemplate: PassTemplate | null;
  UpdateDetails: (
    name: string,
    description: string,
    pointsToCollect: number,
    rewardName: string,
    rewardType: "Stamp" | "CouponValue",
    couponValue: number | null,
    currencyCode: string | null,
  ) => void;
  showPointsInput?: boolean;
}

const DetailsForm: React.FC<DetailsProps> = ({
  activeTab, passTemplate, UpdateDetails, showPointsInput = false,
}) => {
  const [name, setName] = useState(passTemplate?.name || "");
  const [description, setDescription] = useState(passTemplate?.description || "");
  const [pointsToCollect, setPointsToCollect] = useState(passTemplate?.pointsToCollect || 4);
  const [rewardName, setRewardName] = useState(passTemplate?.rewardName || "");
  const [rewardType, setRewardType] = useState<"Stamp" | "CouponValue">(passTemplate?.rewardType || "Stamp");
  const [couponValue, setCouponValue] = useState(passTemplate?.couponValue || 25);
  const [currencyCode, setCurrencyCode] = useState(passTemplate?.currencyCode || "SAR");
  const { t } = useTranslation();

  const inputClass = "w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent transition";
  const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";

  return (
    <div className={activeTab === "details" ? "block space-y-4 pt-4" : "hidden"}>

      <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 space-y-4">
        <div>
          <label className={labelClass}>{t("name")}</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t("namePlaceholder")}
            className={inputClass}
          />
        </div>

        <div>
          <label className={labelClass}>{t("description")}</label>
          <textarea
            rows={3}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder={t("pointsToCollectPlaceholder")}
            className={`${inputClass} resize-none`}
          />
        </div>

        {showPointsInput && (
          <div>
            <label className={labelClass}>{t("rewardType")}</label>
            <select
              value={rewardType}
              onChange={e => setRewardType(e.target.value as "Stamp" | "CouponValue")}
              className={inputClass}
            >
              <option value="Stamp">{t("rewardTypeStamp")}</option>
              <option value="CouponValue">{t("rewardTypeCouponValue")}</option>
            </select>
          </div>
        )}

        {showPointsInput && (
          <div>
            <label className={labelClass}>{t("pointsToCollect")}</label>
            <input
              type="number"
              value={pointsToCollect}
              onChange={e => setPointsToCollect(Number(e.target.value))}
              min={2}
              max={rewardType === "CouponValue" ? 100000 : 30}
              className={inputClass}
            />
          </div>
        )}

        {showPointsInput && rewardType === "CouponValue" && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>{t("couponValue")}</label>
              <input
                type="number"
                value={couponValue}
                onChange={e => setCouponValue(Number(e.target.value))}
                min={0}
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>{t("currencyCode")}</label>
              <input
                type="text"
                value={currencyCode}
                onChange={e => setCurrencyCode(e.target.value.toUpperCase())}
                placeholder="SAR"
                maxLength={3}
                className={inputClass}
              />
            </div>
          </div>
        )}

        <div>
          <label className={labelClass}>{t("rewardName")}</label>
          <input
            type="text"
            value={rewardName}
            onChange={e => setRewardName(e.target.value)}
            placeholder={t("rewardNamePlaceholder")}
            className={inputClass}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => UpdateDetails(
          name, description, pointsToCollect, rewardName,
          rewardType, rewardType === "CouponValue" ? couponValue : null, rewardType === "CouponValue" ? currencyCode : null,
        )}
        className="w-full py-3 rounded-xl bg-brand-600 text-white text-sm font-bold hover:bg-brand-700 transition active:scale-95"
      >
        {t("Next")}
      </button>
    </div>
  );
};

export default DetailsForm;
